import React, { useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { GraduationCap, ClipboardList, Award, LogOut, Globe, Menu, X, BookOpen, Radio } from 'lucide-react';

// Student Pages
import StudentHome from '../components/student/StudentHome';
import AvailableExams from '../components/student/AvailableExams';
import TakeExam from '../components/student/TakeExam';
import MyResults from '../components/student/MyResults';
import ResultDetails from '../components/student/ResultDetails';
import MyCourses from '../components/student/MyCourses';
import LiveStreamView from '../components/student/LiveStreamView';
import Notifications from '../components/student/Notifications';
import NotificationBell from '../components/student/NotificationBell';
import MyCertificates from '../components/student/MyCertificates';

const StudentDashboard = ({ user, onLogout, onToggleLanguage }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isRTL = i18n.language === 'ar';

  const navItems = [
    { path: '/student', icon: GraduationCap, label: t('dashboard'), exact: true },
    { path: '/student/exams', icon: ClipboardList, label: t('availableExams') },
    { path: '/student/courses', icon: BookOpen, label: t('myCourses') },
    { path: '/student/live-stream', icon: Radio, label: t('liveStream') },
    { path: '/student/results', icon: Award, label: t('myResults') },
    { path: '/student/certificates', icon: Award, label: isRTL ? 'شهاداتي' : 'My Certificates' }
  ];

  const isActive = (path, exact) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-indigo-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                data-testid="menu-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <img src="/logo.png" alt={t('appFullName')} className="h-10 w-10 object-contain" />
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden sm:block text-sm text-gray-600">
                <span className="font-medium">{user.name}</span>
                <span className="mx-2">•</span>
                <span className="text-cyan-600">{t('student')}</span>
              </div>
              
              {/* Notification Bell */}
              <NotificationBell />
              
              <Button
                data-testid="language-toggle-dashboard"
                onClick={onToggleLanguage}
                variant="outline"
                size="icon"
                className="rounded-full"
              >
                <Globe className="h-5 w-5" />
              </Button>
              <Button
                data-testid="logout-button"
                onClick={onLogout}
                variant="outline"
                size="icon"
                className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-16 left-0 h-[calc(100vh-4rem)] w-64 bg-white border-r border-gray-200 shadow-lg
            transform transition-transform duration-300 ease-in-out z-40
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          <nav className="p-4 space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all
                    ${active
                      ? 'bg-gradient-to-r from-cyan-600 to-indigo-600 text-white shadow-lg'
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8">
          <Routes>
            <Route path="/" element={<StudentHome />} />
            <Route path="/exams" element={<AvailableExams />} />
            <Route path="/exams/:examId" element={<TakeExam />} />
            <Route path="/courses" element={<MyCourses />} />
            <Route path="/live-stream" element={<LiveStreamView />} />
            <Route path="/results" element={<MyResults />} />
            <Route path="/results/:submissionId" element={<ResultDetails />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/certificates" element={<MyCertificates />} />
          </Routes>
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default StudentDashboard;